import styles from './Timeline.module.scss';

import type {Scene} from '@cascade/core';
import {useSignal} from '@preact/signals-react';
import clsx from 'clsx';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useApplication, useTimelineContext} from '../../contexts';
import {useAudioEndTime, useScenes, useSubscribableValue} from '../../hooks';
import {useSaaSEditor} from '../../saas/contexts/editor-context';
import {findAndOpenFirstUserFile, formatDuration} from '../../utils';

// ─── Context Menu State (module-level for simplicity) ───
interface ContextMenuState {
  x: number;
  y: number;
  sceneIndex: number;
  sceneName: string;
}

export function SceneTrack() {
  const scenes = useScenes();
  const {player} = useApplication();
  const dragFrom = useSignal<number | null>(null);
  const dragOver = useSignal<number | null>(null);
  const saas = useSaaSEditor();
  const [contextMenu, setContextMenu] = useState<ContextMenuState | null>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const audioEnd = useAudioEndTime();

  // Close context menu on click anywhere (capture phase to beat stopPropagation)
  useEffect(() => {
    if (!contextMenu) return;
    const handler = (e: PointerEvent) => {
      // Don't dismiss if clicking inside the context menu itself
      const menu = document.querySelector(`.${styles.contextMenu}`);
      if (menu?.contains(e.target as Node)) return;
      setContextMenu(null);
    };
    document.addEventListener('pointerdown', handler, true);
    return () => document.removeEventListener('pointerdown', handler, true);
  }, [contextMenu]);

  const handleDragStart = useCallback(
    (index: number) => {
      dragFrom.value = index;
    },
    [],
  );

  const handleDragOver = useCallback(
    (index: number) => {
      if (dragFrom.value !== null && dragFrom.value !== index) {
        dragOver.value = index;
      }
    },
    [],
  );

  const handleDragEnd = useCallback(() => {
    const from = dragFrom.value;
    const to = dragOver.value;
    dragFrom.value = null;
    dragOver.value = null;

    if (from !== null && to !== null && from !== to) {
      player.reorderScenes(from, to);
    }
  }, [player]);

  const handleContextMenu = useCallback(
    (event: MouseEvent, index: number, name: string) => {
      event.preventDefault();
      event.stopPropagation();
      setContextMenu({
        x: event.clientX,
        y: event.clientY,
        sceneIndex: index,
        sceneName: name,
      });
    },
    [],
  );

  const handleRemoveScene = useCallback(() => {
    if (!contextMenu) return;
    const idx = contextMenu.sceneIndex;
    setContextMenu(null);

    if (saas?.onSceneRemoved) {
      saas.onSceneRemoved(idx);
    } else {
      player.removeScene(idx);
    }
  }, [player, contextMenu, saas]);

  // Compute how many seconds the video falls short of audio
  const lastScene = scenes[scenes.length - 1];
  const lastCached = lastScene
    ? lastScene.onCacheChanged.current
    : null;
  const videoDurationSec = lastCached
    ? player.status.framesToSeconds(lastCached.lastFrame)
    : 0;
  const audioShortfall =
    audioEnd !== null && videoDurationSec < audioEnd - 0.1
      ? audioEnd - videoDurationSec
      : 0;

  return (
    <>
      <div ref={trackRef} className={styles.sceneTrack}>
        {scenes.map((scene, index) => (
          <SceneClip
            key={scene.name}
            scene={scene}
            index={index}
            isDragging={dragFrom.value === index}
            isDropTarget={
              dragFrom.value !== null &&
              dragOver.value === index &&
              dragFrom.value !== index
            }
            dropSide={
              dragFrom.value !== null && dragOver.value === index
                ? dragFrom.value < index
                  ? 'right'
                  : 'left'
                : null
            }
            audioShortfall={index === scenes.length - 1 ? audioShortfall : 0}
            onDragStart={handleDragStart}
            onDragOver={handleDragOver}
            onDragEnd={handleDragEnd}
            onContextMenu={handleContextMenu}
          />
        ))}
      </div>
      {contextMenu && (
        <SceneContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          sceneName={contextMenu.sceneName}
          canDelete={scenes.length > 1}
          onRemove={handleRemoveScene}
          onClose={() => setContextMenu(null)}
        />
      )}
    </>
  );
}

// ─── Context Menu Component ───

interface SceneContextMenuProps {
  x: number;
  y: number;
  sceneName: string;
  canDelete: boolean;
  onRemove: () => void;
  onClose: () => void;
}

function SceneContextMenu({
  x,
  y,
  sceneName,
  canDelete,
  onRemove,
  onClose,
}: SceneContextMenuProps) {
  const menuRef = useRef<HTMLDivElement>(null);

  return (
    <div
      ref={menuRef}
      className={styles.contextMenu}
      style={{left: `${x}px`, top: `${y}px`}}
      onPointerDown={e => e.stopPropagation()}
    >
      <div className={styles.contextMenuHeader}>{sceneName}</div>
      <button
        className={styles.contextMenuItem}
        disabled={!canDelete}
        onPointerUp={() => {
          if (canDelete) onRemove();
        }}
      >
        Remove from project
      </button>
      <button
        className={styles.contextMenuItem}
        onPointerUp={onClose}
      >
        Cancel
      </button>
    </div>
  );
}

// ─── Scene Clip Colors ───

const SCENE_COLORS = [
  '#4f9eff', '#7c6ff7', '#f97066', '#34d399',
  '#f59e0b', '#ec4899', '#06b6d4', '#a78bfa',
];

function hashSceneName(name: string): number {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = ((hash << 5) - hash + name.charCodeAt(i)) | 0;
  }
  return Math.abs(hash);
}

// ─── Scene Clip Component ───

interface SceneClipProps {
  scene: Scene;
  index: number;
  isDragging: boolean;
  isDropTarget: boolean;
  dropSide: 'left' | 'right' | null;
  audioShortfall: number;
  onDragStart: (index: number) => void;
  onDragOver: (index: number) => void;
  onDragEnd: () => void;
  onContextMenu: (event: MouseEvent, index: number, name: string) => void;
}

function SceneClip({
  scene,
  index,
  isDragging,
  isDropTarget,
  dropSide,
  audioShortfall,
  onDragStart,
  onDragOver,
  onDragEnd,
  onContextMenu,
}: SceneClipProps) {
  const {player, meta} = useApplication();
  const {framesToPercents, framesToPixels, offset} = useTimelineContext();
  const cachedData = useSubscribableValue(scene.onCacheChanged);
  const clipRef = useRef<HTMLDivElement>(null);
  const dragActive = useRef(false);

  const clipAccent = SCENE_COLORS[hashSceneName(scene.name) % SCENE_COLORS.length];

  const nameStyle = useMemo(() => {
    const sceneOffset = framesToPixels(cachedData.firstFrame);
    return offset > sceneOffset
      ? {paddingLeft: `${offset - sceneOffset}px`}
      : {};
  }, [offset, cachedData.firstFrame, framesToPixels]);

  return (
    <div
      ref={clipRef}
      className={clsx(
        styles.clip,
        isDragging && styles.clipDragging,
        isDropTarget && dropSide === 'left' && styles.clipDropLeft,
        isDropTarget && dropSide === 'right' && styles.clipDropRight,
      )}
      style={{
        width: `${framesToPercents(cachedData.duration)}%`,
        '--clip-accent': clipAccent,
      } as any}
      onContextMenu={event => {
        onContextMenu(event as unknown as MouseEvent, index, scene.name);
      }}
      onPointerDown={event => {
        // Middle click: set range
        if (event.button === 1) {
          event.preventDefault();
          return;
        }
        // Right click: let context menu handle it
        if (event.button === 2) return;
        // Left click on the clip background: start drag
        // Don't start drag on the name link (it has stopPropagation)
        dragActive.current = false;
      }}
      onPointerMove={event => {
        if (
          event.buttons === 1 &&
          !dragActive.current &&
          clipRef.current?.hasPointerCapture(event.pointerId)
        ) {
          // Drag started after some movement
          dragActive.current = true;
          onDragStart(index);
        }
        if (dragActive.current) {
          // Determine which clip we're hovering over by checking siblings
          const track = clipRef.current?.parentElement;
          if (!track) return;
          const children = Array.from(track.children) as HTMLElement[];
          for (let i = 0; i < children.length; i++) {
            const rect = children[i].getBoundingClientRect();
            if (event.clientX >= rect.left && event.clientX <= rect.right) {
              onDragOver(i);
              break;
            }
          }
        }
      }}
      onPointerUp={event => {
        if (event.button === 1) {
          event.stopPropagation();
          meta.shared.range.set([
            player.status.framesToSeconds(cachedData.firstFrame),
            player.status.framesToSeconds(cachedData.lastFrame),
          ]);
          return;
        }
        if (dragActive.current) {
          dragActive.current = false;
          clipRef.current?.releasePointerCapture(event.pointerId);
          onDragEnd();
        }
      }}
    >
      <div className={styles.container}>
        <div
          className={styles.name}
          style={nameStyle}
          title="Go to source"
          onPointerDown={event => {
            event.stopPropagation();
            clipRef.current?.setPointerCapture(event.pointerId);
          }}
          onPointerUp={async event => {
            event.stopPropagation();
            if (!dragActive.current && scene.creationStack) {
              await findAndOpenFirstUserFile(scene.creationStack);
            }
          }}
        >
          {scene.name}
        </div>
        {audioShortfall > 0 && (
          <div
            className={styles.audioShortfall}
            title={`Video is ${formatDuration(audioShortfall)} shorter than audio`}
          >
            -{formatDuration(audioShortfall)}
          </div>
        )}
      </div>
    </div>
  );
}
